/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type PropsWithChildren, useMemo, useRef } from 'react';
import { type Navigator, parsePath, Router } from 'react-router-dom';
import { toPath } from './appPaths';

export type DesktopNavigate = (path: string, state: unknown, replace: boolean) => void;

export interface DesktopRouterLocation {
  key: string;
  path: string;
  state?: unknown;
}

/**
 * A react-router `<Router>` whose location lives in desktop state instead of browser history.
 * `Link`, `useNavigate`, `useParams`, `<Routes>` etc. inside it read and write that location.
 *
 * Desktop routers are siblings, never nested: each window gets one, and chrome outside windows
 * uses `DesktopShellRouter`. Nothing above the window layer renders a router.
 */
export function DesktopRouter({
  children,
  location,
  onGo,
  onNavigate,
}: Readonly<
  PropsWithChildren<{
    location: DesktopRouterLocation;
    onGo: (delta: number) => void;
    onNavigate: DesktopNavigate;
  }>
>) {
  // Keep the navigator identity stable so `useNavigate` consumers do not re-run effects.
  const handlers = useRef({ onGo, onNavigate });
  handlers.current = { onGo, onNavigate };
  const navigator = useMemo<Navigator>(
    () => ({
      createHref: toPath,
      go: (delta) => handlers.current.onGo(delta),
      push: (to, state) => handlers.current.onNavigate(toPath(to), state, false),
      replace: (to, state) => handlers.current.onNavigate(toPath(to), state, true),
    }),
    [],
  );
  const routerLocation = useMemo(
    () => ({ ...parsePath(location.path), key: location.key, state: location.state ?? null }),
    [location.key, location.path, location.state],
  );

  return (
    <Router location={routerLocation} navigator={navigator}>
      {children}
    </Router>
  );
}
